/**
 * 65.49: the anti-dropship missile's lock in the play view — HUD view B, the owner's pick (Oct 20: *"Ok for anti-dropship missile, I like hud view B,
 * launcher C"*; design_sheets/anti_dropship_missile.html shows A and C beside it): the seeker's circle round the crosshair, its cross
 * wandering inside it while it searches, on the ship it tracks (amber) and holds (green), a diamond on the ship at the
 * lock, and the state's word under the crosshair. Drawn on a 2D canvas over the view; pure — it reads what it is given.
 *
 * What it needs each frame from the lock (lane F's 65.49 sim, the host's reading of it): the lock's state, the ship it
 * tracks or holds (the host projects that ship's drawn position to the screen: `target`, null when off screen or behind),
 * and the seeker's cone as a radius on screen (`coneRadius`: the host's from the lock cone and the camera's field of
 * view). The tone is sound's.
 */

/** The seeker's states as the HUD shows them: off (no seeker), searching, tracking a ship, locked on it. */
export type AntiDropshipMissileHudState = 'off' | 'search' | 'track' | 'lock';

/** The colours, the cross's half-size, the diamond's, how far the searching cross wanders (shares of the circle) and how fast, the state's word. */
export const ANTI_DROPSHIP_MISSILE_HUD = {
  dim: 'rgba(235,240,245,0.4)', amber: '#ffae38', green: '#6dff74',
  cross: 7, diamond: 10, wander: [0.5, 0.4], wanderRate: [5.3, 3.7],
  words: { off: '', search: 'SEEKER', track: 'TRACK', lock: 'LOCK' } as Record<AntiDropshipMissileHudState, string>,
} as const;

export interface AntiDropshipMissileHudFrame {
  state: AntiDropshipMissileHudState;
  /** the tracked or locked ship on screen (pixels), or null when off screen or behind */
  target: { x: number; y: number } | null;
  /** the seeker's cone as a radius on screen (pixels) */
  coneRadius: number;
  /** seconds, any clock (the searching cross's wander) */
  t: number;
}

/** The state's colour. */
export function antiDropshipMissileHudColour(state: AntiDropshipMissileHudState): string {
  return state === 'lock' ? ANTI_DROPSHIP_MISSILE_HUD.green : state === 'track' ? ANTI_DROPSHIP_MISSILE_HUD.amber : ANTI_DROPSHIP_MISSILE_HUD.dim;
}

/** Where the seeker's cross is drawn: wandering inside the circle while it searches, on the ship once it tracks it; null when none is drawn. */
export function antiDropshipMissileHudCross(f: AntiDropshipMissileHudFrame, w: number, h: number): { x: number; y: number } | null {
  const cx = w / 2, cy = h / 2, H = ANTI_DROPSHIP_MISSILE_HUD;
  if (f.state === 'off') return null;
  if (f.state === 'search') return { x: cx + Math.sin(f.t * H.wanderRate[0]) * f.coneRadius * H.wander[0], y: cy + Math.cos(f.t * H.wanderRate[1]) * f.coneRadius * H.wander[1] };
  return f.target;
}

/** Draw view B on `g` (a `w` × `h` canvas over the view, the crosshair at its middle). Nothing when the seeker is off. */
export function drawAntiDropshipMissileHud(g: CanvasRenderingContext2D, f: AntiDropshipMissileHudFrame, w: number, h: number): void {
  if (f.state === 'off') return;
  const cx = w / 2, cy = h / 2, H = ANTI_DROPSHIP_MISSILE_HUD, col = antiDropshipMissileHudColour(f.state);
  g.strokeStyle = col; g.lineWidth = 1.5;
  g.beginPath(); g.arc(cx, cy, f.coneRadius, 0, Math.PI * 2); g.stroke();
  const at = antiDropshipMissileHudCross(f, w, h);
  if (at) {
    g.lineWidth = 2; g.beginPath();
    g.moveTo(at.x - H.cross, at.y); g.lineTo(at.x + H.cross, at.y); g.moveTo(at.x, at.y - H.cross); g.lineTo(at.x, at.y + H.cross);
    g.stroke();
    if (f.state === 'lock') { g.beginPath(); g.moveTo(at.x, at.y - H.diamond); g.lineTo(at.x + H.diamond, at.y); g.lineTo(at.x, at.y + H.diamond); g.lineTo(at.x - H.diamond, at.y); g.closePath(); g.stroke(); }
  }
  g.font = '600 11px system-ui'; g.fillStyle = col; g.textAlign = 'center';
  g.fillText(H.words[f.state], cx, cy + 40);
}
